// Short Web Audio beep for continuous-scan detection feedback.
//
// Both PWAs auto-start their scan on mount, so there is no scan-start
// tap to piggyback AudioContext unlock on. Instead we register a
// one-shot `pointerdown` capture listener on `document` that resumes
// the context on the user's first interaction anywhere on the page.
// If the user never taps, `play()` no-ops silently — the scan still
// works, just without audio confirmation.
//
// A visibilitychange listener re-resumes the context on foreground.
// Chrome auto-suspends AudioContexts in background tabs after ~30s;
// without this, coming back to the tab means silent no-ops for the
// rest of the session.
//
// iOS Safari respects the hardware silent switch on Web Audio output;
// this is documented in the plan as expected behavior.
//
// Non-blocking: `play()` schedules the oscillator and returns
// synchronously. No `await` on the hot path — the ~5 FPS decode budget
// is tight (see docs/solutions/qr-distance/zxing-maxnumberofsymbols-tryharder-perf-footgun.md).
//
// Mirror lives at web-vanilla/lib/beep-player.js.

const FREQUENCY_HZ = 1000;
const DURATION_MS = 80;
const GAIN_PEAK = 0.15;

export interface BeepPlayer {
  play(): void;
  dispose(): void;
}

type AudioCtor = typeof AudioContext;

function resolveAudioContextCtor(): AudioCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    AudioContext?: AudioCtor;
    webkitAudioContext?: AudioCtor;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

export function createBeepPlayer(): BeepPlayer {
  const Ctor = resolveAudioContextCtor();
  let ctx: AudioContext | null = null;
  let disposed = false;

  function ensureContext(): AudioContext | null {
    if (disposed || !Ctor) return null;
    if (ctx) return ctx;
    try {
      ctx = new Ctor();
    } catch {
      ctx = null;
    }
    return ctx;
  }

  function unlock(): void {
    const c = ensureContext();
    if (!c) return;
    if (c.state === 'suspended') {
      c.resume().catch(() => {
        /* stays suspended; play() will no-op */
      });
    }
  }

  const unlockHandler = () => unlock();
  const visibilityHandler = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      unlock();
    }
  };

  if (typeof document !== 'undefined' && Ctor) {
    // DO NOT add preventDefault/stopPropagation to unlockHandler — this
    // runs at document capture phase, and either call would silently
    // break every click/tap in the app. `passive: true` enforces this
    // at runtime.
    document.addEventListener('pointerdown', unlockHandler, { once: true, capture: true, passive: true });
    document.addEventListener('visibilitychange', visibilityHandler);
  }

  function play(): void {
    const c = ensureContext();
    if (!c || c.state !== 'running') return;
    try {
      const now = c.currentTime;
      const durationS = DURATION_MS / 1000;
      const osc = c.createOscillator();
      const gain = c.createGain();
      osc.type = 'sine';
      osc.frequency.value = FREQUENCY_HZ;
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(GAIN_PEAK, now + 0.005);
      gain.gain.linearRampToValueAtTime(0, now + durationS);
      osc.connect(gain).connect(c.destination);
      osc.start(now);
      osc.stop(now + durationS + 0.01);
    } catch {
      /* fire-and-forget; a failed beep must never break the scan */
    }
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    if (typeof document !== 'undefined') {
      document.removeEventListener('pointerdown', unlockHandler, { capture: true });
      document.removeEventListener('visibilitychange', visibilityHandler);
    }
    if (ctx) {
      try {
        void ctx.close();
      } catch {
        /* already closed */
      }
      ctx = null;
    }
  }

  return { play, dispose };
}
